import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Heart, Sparkles, FileText, Wallet, AlarmClock, Users, Home, Bus, Cpu, HandHeart,
  ArrowRight, ShieldCheck, BookOpen, MessageCircleHeart, CheckCircle2, Circle, Loader2, ExternalLink, Send,
} from "lucide-react";
import { useSeo } from "@/lib/useSeo";

const ICONS = { FileText, Wallet, ClockAlarm: AlarmClock, Users, Home, Bus, Cpu, HandHeart, Heart };

const SITUATIONS = [
  { id: "dys", label: "Troubles dys (dyslexie, dyspraxie, dyscalculie…)" },
  { id: "tdah", label: "TDAH / trouble de l'attention" },
  { id: "anxiete", label: "Troubles anxieux ou dépressifs" },
  { id: "mobilite", label: "Mobilité réduite / fauteuil" },
  { id: "invisible", label: "Handicap invisible (maladie chronique, sensoriel…)" },
  { id: "autre", label: "Autre / pas encore identifié" },
];

const RESOURCES = [
  { title: "etudiant.gouv.fr — Handicap", desc: "Page officielle du ministère : aménagements, contacts relais handicap par université.", url: "https://www.etudiant.gouv.fr/fr/handicap-1290" },
  { title: "MDPH en ligne", desc: "Démarche en ligne pour ton dossier MDPH (RQTH, AAH, PCH, CMI…).", url: "https://mdphenligne.cnsa.fr" },
  { title: "FÉDEEH — Fédération étudiante", desc: "Fédération des étudiants en situation de handicap : conseils, tutorat, ressources.", url: "https://www.fedeeh.org" },
  { title: "Hi-Sup — Hi! Sup", desc: "Réseau d'entraide entre étudiant·e·s en situation de handicap.", url: "https://www.fedeeh.org/programme/hisup" },
  { title: "Santé Psy Étudiant", desc: "12 séances de psy gratuites par an sur prescription médicale.", url: "https://santepsyetudiant.fr" },
  { title: "Mon Soutien Psy (Sécu)", desc: "8 séances remboursées sans avance de frais, pour tous.", url: "https://www.monsoutienpsy.ameli.fr" },
];

export default function AccessibiliteAccompagnement() {
  useSeo({
    title: "Espace handicap & accompagnement étudiant — Student Hub",
    description: "Accompagnement étudiant pour situation de handicap, troubles dys, TDAH, anxiété : simulateur d'aides, IA bienveillante, checklist personnalisée, ressources MDPH et CROUS. Gratuit et confidentiel.",
    keywords: "étudiant handicap France, aides handicap étudiant, MDPH étudiant, aménagement examens, étudiant TDAH, étudiant dys, accessibilité université, RQTH étudiante, AAH étudiant, BAPU",
  });

  return (
    <div className="space-y-12 sm:space-y-16 pb-8" data-testid="accessibilite-page">
      <Hero />
      <Disclaimer />
      <Simulator />
      <ChecklistSection />
      <AIAssistant />
      <Resources />
      <FaqSeo />
    </div>
  );
}

/* ───────────────────────── HERO ───────────────────────── */
function Hero() {
  const scrollTo = (id) => () => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <section className="rounded-3xl bg-gradient-to-br from-violet-50 via-white to-indigo-50 border border-violet-100 p-7 sm:p-12" data-testid="acc-hero">
      <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-violet-700 font-semibold mb-4">
        <Heart size={14} className="text-rose-500" /> Espace inclusif · Gratuit · Confidentiel
      </p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05] max-w-3xl">
        Un accompagnement étudiant pensé pour <span className="bg-gradient-to-r from-violet-600 to-indigo-600 bg-clip-text text-transparent">tous</span>.
      </h1>
      <p className="text-slate-600 mt-5 text-base sm:text-lg max-w-2xl leading-relaxed">
        Démarches simplifiées, aides, accompagnement IA bienveillant et ressources utiles pour t'aider à réussir tes études sereinement — quelle que soit ta situation.
      </p>
      <div className="mt-7 flex flex-col sm:flex-row gap-3">
        <Button onClick={scrollTo("acc-simulator")} className="h-12 px-6 rounded-full bg-violet-600 hover:bg-violet-700 text-base font-semibold" data-testid="acc-cta-simulator">
          <Sparkles size={18} className="mr-2" /> Découvrir mes aides
        </Button>
        <Button onClick={scrollTo("acc-ai")} variant="outline" className="h-12 px-6 rounded-full text-base font-semibold border-slate-300" data-testid="acc-cta-ai">
          <MessageCircleHeart size={18} className="mr-2" /> Parler à l'assistant IA
        </Button>
        <Button onClick={scrollTo("acc-checklist")} variant="outline" className="h-12 px-6 rounded-full text-base font-semibold border-slate-300" data-testid="acc-cta-checklist">
          <CheckCircle2 size={18} className="mr-2" /> Ma checklist
        </Button>
      </div>
      <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-emerald-500" /> Données chiffrées · RGPD</span>
        <span className="inline-flex items-center gap-1.5"><Users size={14} className="text-violet-500" /> Pensé avec des étudiant·e·s concerné·e·s</span>
        <span className="inline-flex items-center gap-1.5"><Heart size={14} className="text-rose-500" /> Sans jugement</span>
      </div>
    </section>
  );
}

/* ───────────────────────── DISCLAIMER ───────────────────────── */
function Disclaimer() {
  return (
    <aside className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-900 text-sm leading-relaxed" data-testid="acc-disclaimer" role="note">
      <strong>À garder en tête :</strong> Student Hub ne remplace pas les services officiels (MDPH, CROUS, université, CAF) ni un·e professionnel·le de santé. Notre rôle est de t'orienter, de vulgariser et de t'aider à organiser tes démarches. En cas d'urgence psychologique, contacte le <strong>3114</strong> (24/24, gratuit, anonyme) ou Nightline au <strong>05 82 95 10 11</strong> (21h-2h30).
    </aside>
  );
}

/* ───────────────────────── SIMULATEUR ───────────────────────── */
function Simulator() {
  const [form, setForm] = useState({
    age: 20, type_etudes: "Licence", ville: "", boursier: false,
    logement: "indifferent", situation: [], mobilite_reduite: false,
    alternance: false, revenus_mensuels: 0,
  });
  const [loading, setLoading] = useState(false);
  const [aides, setAides] = useState(null);
  const resultsRef = useRef(null);

  const toggleSituation = (id) => {
    setForm((f) => ({
      ...f,
      situation: f.situation.includes(id) ? f.situation.filter((x) => x !== id) : [...f.situation, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.ville.trim()) { toast.error("Indique au moins ta ville"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/accessibility/simulate", form);
      setAides(data.aides || []);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur, réessaie");
    } finally { setLoading(false); }
  };

  return (
    <section id="acc-simulator" data-testid="acc-simulator">
      <header className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-violet-700 font-semibold mb-2">Simulateur intelligent</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Trouve les aides qui correspondent à ta situation</h2>
        <p className="text-slate-600 mt-2 max-w-2xl">Réponds à quelques questions simples. On te propose une liste personnalisée d'aides, d'accompagnements et de démarches utiles — en 30 secondes.</p>
      </header>

      <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-6" data-testid="acc-simulator-form">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="age" className="text-sm">Âge</Label>
            <Input id="age" type="number" min={15} max={99} value={form.age}
                   onChange={(e) => setForm({ ...form, age: parseInt(e.target.value) || 18 })}
                   className="mt-1.5 h-11" data-testid="sim-age" />
          </div>
          <div>
            <Label className="text-sm">Niveau d'études</Label>
            <Select value={form.type_etudes} onValueChange={(v) => setForm({ ...form, type_etudes: v })}>
              <SelectTrigger className="mt-1.5 h-11" data-testid="sim-etudes"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Lycée / Terminale", "BTS / DUT", "Licence", "Master", "École d'ingénieur / Commerce", "Doctorat", "Autre"].map((x) => (
                  <SelectItem key={x} value={x}>{x}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="ville" className="text-sm">Ta ville</Label>
            <Input id="ville" required value={form.ville}
                   onChange={(e) => setForm({ ...form, ville: e.target.value })}
                   placeholder="Lyon, Paris 13, Toulouse..."
                   className="mt-1.5 h-11" data-testid="sim-ville" />
          </div>
          <div>
            <Label className="text-sm">Type de logement</Label>
            <Select value={form.logement} onValueChange={(v) => setForm({ ...form, logement: v })}>
              <SelectTrigger className="mt-1.5 h-11" data-testid="sim-logement"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="indifferent">Pas encore décidé / indifférent</SelectItem>
                <SelectItem value="crous">CROUS / résidence étudiante</SelectItem>
                <SelectItem value="prive">Studio / appart privé</SelectItem>
                <SelectItem value="parents">Chez mes parents</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="rev" className="text-sm">Revenus mensuels personnels (€)</Label>
            <Input id="rev" type="number" min={0} max={10000} value={form.revenus_mensuels}
                   onChange={(e) => setForm({ ...form, revenus_mensuels: parseInt(e.target.value) || 0 })}
                   className="mt-1.5 h-11" data-testid="sim-revenus" />
            <p className="text-xs text-slate-500 mt-1">Indicatif. Mets 0 si tu n'as pas encore de revenus.</p>
          </div>
          <div className="flex flex-col gap-3 sm:pt-7">
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox checked={form.boursier} onCheckedChange={(v) => setForm({ ...form, boursier: !!v })} data-testid="sim-boursier" />
              <span className="text-sm text-slate-700">Je suis boursier·ère CROUS (ou je vais le demander)</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox checked={form.alternance} onCheckedChange={(v) => setForm({ ...form, alternance: !!v })} data-testid="sim-alternance" />
              <span className="text-sm text-slate-700">Je suis (ou serai) en alternance</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox checked={form.mobilite_reduite} onCheckedChange={(v) => setForm({ ...form, mobilite_reduite: !!v })} data-testid="sim-mobilite" />
              <span className="text-sm text-slate-700">Mobilité réduite / fauteuil</span>
            </label>
          </div>
        </div>

        <div>
          <Label className="text-sm">Ta situation (plusieurs choix possibles, ou aucun)</Label>
          <div className="mt-3 grid sm:grid-cols-2 gap-2">
            {SITUATIONS.map((s) => {
              const active = form.situation.includes(s.id);
              return (
                <button key={s.id} type="button" onClick={() => toggleSituation(s.id)}
                        data-testid={`sim-situation-${s.id}`}
                        className={`text-left rounded-xl border px-4 py-3 text-sm transition-all ${active ? "border-violet-500 bg-violet-50 text-violet-900" : "border-slate-200 hover:border-slate-300 text-slate-700"}`}>
                  <span className="inline-flex items-center gap-2">
                    {active ? <CheckCircle2 size={16} className="text-violet-600 shrink-0" /> : <Circle size={16} className="text-slate-400 shrink-0" />}
                    {s.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <Button type="submit" disabled={loading} className="h-12 px-7 rounded-full bg-violet-600 hover:bg-violet-700 text-base font-semibold" data-testid="sim-submit">
          {loading ? <><Loader2 size={18} className="mr-2 animate-spin" /> Analyse en cours…</> : <><Sparkles size={18} className="mr-2" /> Voir mes aides personnalisées</>}
        </Button>
      </form>

      <div ref={resultsRef} className="mt-8" data-testid="acc-results">
        {aides !== null && aides.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-slate-500">
            Coche au moins une situation ou indique tes besoins pour voir les aides correspondantes. Tu peux aussi <a href="#acc-ai" className="text-violet-600 underline">parler à l'IA</a> directement.
          </div>
        )}
        {aides !== null && aides.length > 0 && (
          <>
            <h3 className="font-display text-2xl font-bold text-slate-900 mb-4">{aides.length} aide{aides.length > 1 ? "s" : ""} potentielle{aides.length > 1 ? "s" : ""} pour toi</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {aides.map((a) => {
                const Icon = ICONS[a.icon] || Heart;
                return (
                  <article key={a.id} className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col gap-3" data-testid={`acc-aide-${a.id}`}>
                    <div className="w-11 h-11 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center"><Icon size={20} /></div>
                    <h4 className="font-display font-semibold text-slate-900 text-lg leading-snug">{a.title}</h4>
                    <p className="text-sm text-slate-600 leading-relaxed">{a.why}</p>
                    <ol className="text-sm text-slate-700 space-y-1.5 pl-5 list-decimal">
                      {(a.steps || []).map((s, i) => <li key={i}>{s}</li>)}
                    </ol>
                    {a.link && (
                      <a href={a.link} target="_blank" rel="noopener noreferrer"
                         className="text-sm text-violet-600 hover:text-violet-700 font-medium inline-flex items-center gap-1 mt-auto">
                        Lien officiel <ExternalLink size={13} />
                      </a>
                    )}
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/* ───────────────────────── CHECKLIST ───────────────────────── */
function ChecklistSection() {
  const [data, setData] = useState({ items: [], total: 0, done: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/accessibility/checklist");
      setData(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (item) => {
    const next = item.status === "done" ? "not_started" : "done";
    try {
      await api.post("/demarches/toggle", { demarche_id: item.id, status: next });
      load();
      if (next === "done") toast.success("Bravo, étape validée ! 🎉");
    } catch (e) {
      toast.error("Erreur, réessaie");
    }
  };

  const pct = data.total > 0 ? Math.round((data.done / data.total) * 100) : 0;

  return (
    <section id="acc-checklist" data-testid="acc-checklist">
      <header className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-violet-700 font-semibold mb-2">Ta progression</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Ma checklist accompagnement</h2>
        <p className="text-slate-600 mt-2 max-w-2xl">10 étapes simples pour avancer à ton rythme. Tu peux cocher au fur et à mesure — on garde ta progression.</p>
      </header>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
        <div className="flex items-center justify-between mb-5">
          <span className="text-sm text-slate-600"><strong className="text-slate-900">{data.done}</strong> sur {data.total} étapes</span>
          <span className="text-sm font-semibold text-violet-700">{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <ul className="mt-6 space-y-2">
          {loading ? (
            <li className="text-slate-400 text-sm">Chargement…</li>
          ) : (
            data.items.map((it) => {
              const done = it.status === "done";
              return (
                <li key={it.id}>
                  <button onClick={() => toggle(it)} data-testid={`acc-check-${it.id}`}
                          className={`w-full text-left flex items-center gap-3 rounded-xl border p-4 transition-colors ${done ? "border-emerald-200 bg-emerald-50" : "border-slate-200 hover:border-slate-300 bg-white"}`}>
                    {done ? <CheckCircle2 size={20} className="text-emerald-500 shrink-0" /> : <Circle size={20} className="text-slate-400 shrink-0" />}
                    <span className="flex-1">
                      <span className={`block font-medium ${done ? "text-emerald-900 line-through decoration-emerald-300" : "text-slate-900"}`}>{it.title}</span>
                      <span className="block text-xs text-slate-500 mt-0.5">{it.category}</span>
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </section>
  );
}

/* ───────────────────────── AI ASSISTANT ───────────────────────── */
function AIAssistant() {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Salut 👋 Je suis ton assistant accompagnement Student Hub. Je peux t'aider sur les démarches MDPH, les aménagements d'examens, les aides financières (AAH, PCH, bourses), les logements adaptés, le stress étudiant et plus. Pose-moi ta question — sans jugement, sans jargon." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const msg = input.trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/accessibility/chat", { message: msg, session_id: sessionId });
      if (!sessionId && data.session_id) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.message }]);
    } catch (e) {
      const detail = e.response?.data?.detail || "Désolé, l'assistant est momentanément indisponible. Réessaie dans un instant.";
      setMessages((m) => [...m, { role: "assistant", text: detail, error: true }]);
    } finally { setLoading(false); }
  };

  const suggestions = [
    "Comment faire mon dossier MDPH ?",
    "J'ai un TDAH, quels aménagements demander en fac ?",
    "Comment obtenir un logement CROUS adapté ?",
    "Je me sens dépassé·e, que faire ?",
  ];

  return (
    <section id="acc-ai" data-testid="acc-ai">
      <header className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-violet-700 font-semibold mb-2">Assistant IA bienveillant</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Parle-moi de ta situation</h2>
        <p className="text-slate-600 mt-2 max-w-2xl">Un assistant spécialement entraîné pour t'orienter sur les démarches handicap étudiant : MDPH, aides, aménagements, logement, transport, bien-être. Confidentiel, sans jugement, disponible 24/7.</p>
      </header>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden flex flex-col" style={{ minHeight: 480 }}>
        <div className="flex-1 px-4 sm:px-6 py-5 space-y-3 overflow-y-auto" style={{ maxHeight: 480 }}>
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-violet-600 text-white" : m.error ? "bg-red-50 text-red-800 border border-red-200" : "bg-slate-100 text-slate-900"}`}>
                <p className="whitespace-pre-line">{m.text}</p>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-slate-100 rounded-2xl px-4 py-3 inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin text-slate-500" />
                <span className="text-xs text-slate-500">Je réfléchis…</span>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
        {messages.length === 1 && (
          <div className="px-4 sm:px-6 pb-3 flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button key={s} onClick={() => setInput(s)} data-testid="acc-ai-suggest"
                      className="text-xs px-3 py-1.5 rounded-full bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-100">
                {s}
              </button>
            ))}
          </div>
        )}
        <form onSubmit={send} className="border-t border-slate-100 p-3 sm:p-4 flex items-end gap-2" data-testid="acc-ai-form">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1}
            placeholder="Pose ta question (Entrée pour envoyer)…"
            className="flex-1 resize-none rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-violet-400"
            data-testid="acc-ai-input"
          />
          <Button type="submit" disabled={loading || !input.trim()} className="h-11 px-4 rounded-full bg-violet-600 hover:bg-violet-700" data-testid="acc-ai-send">
            <Send size={16} />
          </Button>
        </form>
      </div>
      <p className="text-xs text-slate-500 mt-3 leading-relaxed">L'assistant peut faire des erreurs : vérifie toujours auprès des organismes officiels (MDPH, CROUS, université). Tes conversations restent privées et ne servent jamais à entraîner un modèle.</p>
    </section>
  );
}

/* ───────────────────────── RESOURCES ───────────────────────── */
function Resources() {
  return (
    <section id="acc-resources" data-testid="acc-resources">
      <header className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-violet-700 font-semibold mb-2">Ressources & guides officiels</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Les bons liens à connaître</h2>
        <p className="text-slate-600 mt-2 max-w-2xl">Sources officielles vérifiées. À garder dans tes favoris.</p>
      </header>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {RESOURCES.map((r) => (
          <a key={r.url} href={r.url} target="_blank" rel="noopener noreferrer"
             className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-violet-300 hover:shadow-sm transition-all"
             data-testid={`acc-resource-${r.url}`}>
            <div className="flex items-start justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center">
                <BookOpen size={18} />
              </div>
              <ExternalLink size={14} className="text-slate-400 group-hover:text-violet-600" />
            </div>
            <h3 className="font-display font-semibold text-slate-900 group-hover:text-violet-700 transition-colors">{r.title}</h3>
            <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{r.desc}</p>
          </a>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────────── FAQ SEO ───────────────────────── */
function FaqSeo() {
  const faq = [
    { q: "Qui peut bénéficier de cet espace ?", a: "Tout·e étudiant·e en France : en situation de handicap (visible ou invisible), avec un trouble dys, un TDAH, un trouble anxieux, à mobilité réduite, ou ayant simplement besoin d'un accompagnement adapté." },
    { q: "Faut-il avoir une RQTH pour avoir accès aux aménagements ?", a: "Non. La RQTH (Reconnaissance Qualité Travailleur Handicapé) est utile mais pas obligatoire pour obtenir des aménagements d'examens. Un certificat médical et un RDV avec le·la référent·e handicap de ta fac suffisent dans la plupart des cas." },
    { q: "Comment fonctionne le simulateur d'aides ?", a: "Tu réponds à quelques questions (âge, études, ville, situation, revenus). Notre système te propose une liste personnalisée d'aides financières (AAH, bourses, PCH), d'aménagements et de démarches utiles. Aucune donnée n'est partagée avec un tiers." },
    { q: "L'assistant IA est-il confidentiel ?", a: "Oui. Tes conversations ne sont jamais utilisées pour entraîner un modèle. Tu peux supprimer ton compte (et toutes tes données) en 1 clic depuis tes paramètres." },
    { q: "Que faire en cas d'urgence psychologique ?", a: "Contacte le 3114 (24/24, gratuit, anonyme) ou Nightline France au 05 82 95 10 11 (21h-2h30). En cas de danger vital immédiat, compose le 15 (SAMU) ou le 112." },
  ];
  // Inject JSON-LD FAQPage for SEO
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "acc-faq-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faq.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    });
    document.head.appendChild(script);
    return () => { try { document.head.removeChild(script); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <section data-testid="acc-faq">
      <header className="mb-6">
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Questions fréquentes</h2>
      </header>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-5">
        {faq.map((f, i) => (
          <div key={i} data-testid={`acc-faq-${i}`}>
            <p className="font-semibold text-slate-900">{f.q}</p>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">{f.a}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
